import { useEffect, useRef, useState, type ClipboardEvent, type KeyboardEvent } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { AuthSurface, Button, Alert } from '@sakhtar/design-system/react';
import { verifyMfaLogin } from '../../lib/auth.js';
import { ApiError } from '../../lib/apiClient.js';
import { tenantBasePath } from '../../lib/tenant.js';

const OTP_LENGTH = 6;

/**
 * Real TTL of the challenge this page verifies — mirrored, not fetched: the
 * `mfa_required` response (apps/api/src/auth/auth.controller.ts) forwards
 * only `preSessionId`/`challengeId`, never an expiry, so this constant must
 * be kept in sync by hand with `ttlDate(this.#now(), 300)` in
 * `packages/auth/src/service.ts`'s `issueMfaChallengeRecord`. It drives the
 * countdown only — the backend's own `expires_at` is what actually rejects
 * a stale code (as `NOT_FOUND`, see the error handling below).
 */
const CHALLENGE_TTL_SECONDS = 300;

interface OtpLocationState {
  readonly preSessionId?: string;
  readonly challengeId?: string;
  readonly mobileNumber?: string;
}

function formatCountdown(totalSeconds: number): string {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

/**
 * UI_REFERENCE_SOURCE:
 * section: Sakhtar Pilot P0 §2.5.2 OTP VERIFY · خارج از SHELL · mobile 390
 * canonical pattern: AuthSurface, centered — 6-box code entry, single
 *   primary action, resend-with-countdown
 * state reference: default, validation error (VALIDATION_FAILED), invalid/
 *   expired code (NOT_FOUND — same collapsed-answer convention as LoginPage,
 *   ADR-001 §2.3), rate_limited
 *
 * Real backend only: verifies against `POST .../session/mfa`
 * (apps/api/src/auth/auth.controller.ts), which is already live and tested —
 * this page was the missing half of a flow LoginPage already navigated to
 * (`/login/otp`) with no route ever registered for it.
 *
 * Resend is real, not fabricated: no dedicated resend endpoint exists
 * (the OTP is a "provider boundary fixture" per
 * `packages/auth/src/service.ts`'s `IssuedMfaChallenge` — no SMS provider is
 * wired up yet), and this page never holds the password needed to call
 * `login()` again itself. So once the countdown clears, "ارسال دوباره"
 * routes back to the login screen — the one real way to obtain a fresh
 * challenge — instead of pretending to trigger a new SMS from here.
 */
export function OtpVerifyPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const state = (location.state ?? {}) as OtpLocationState;

  const [digits, setDigits] = useState<readonly string[]>(Array(OTP_LENGTH).fill(''));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [secondsLeft, setSecondsLeft] = useState(CHALLENGE_TTL_SECONDS);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const loginPath = slug ? `${tenantBasePath(slug)}/login` : '';

  useEffect(() => {
    if (!state.preSessionId || !state.challengeId) {
      navigate(loginPath, { replace: true });
    }
    // Only checked on mount — a page arrived at without this state is a
    // stale reload/direct link, not something that becomes valid later.
  }, []);

  useEffect(() => {
    if (secondsLeft <= 0) return;
    const timer = setInterval(() => setSecondsLeft((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(timer);
  }, [secondsLeft]);

  if (!slug) return null;
  const tenantSlug = slug;

  function setDigitAt(index: number, value: string): void {
    setDigits((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  }

  function onDigitChange(index: number, rawValue: string): void {
    const digit = rawValue.replace(/\D/g, '').slice(-1);
    setDigitAt(index, digit);
    if (digit && index < OTP_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  }

  function onDigitKeyDown(index: number, event: KeyboardEvent<HTMLInputElement>): void {
    if (event.key === 'Backspace' && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
      setDigitAt(index - 1, '');
    }
  }

  function onPaste(event: ClipboardEvent<HTMLInputElement>): void {
    const pasted = event.clipboardData.getData('text').replace(/\D/g, '').slice(0, OTP_LENGTH);
    if (!pasted) return;
    event.preventDefault();
    setDigits((prev) => {
      const next = [...prev];
      for (let i = 0; i < pasted.length; i += 1) next[i] = pasted[i] ?? '';
      return next;
    });
    inputRefs.current[Math.min(pasted.length, OTP_LENGTH - 1)]?.focus();
  }

  async function onSubmit(): Promise<void> {
    setError(null);
    const otp = digits.join('');
    if (otp.length !== OTP_LENGTH) {
      setError('کد ۶ رقمی را کامل وارد کنید.');
      return;
    }
    const { preSessionId, challengeId } = state;
    if (!preSessionId || !challengeId) {
      navigate(loginPath, { replace: true });
      return;
    }
    setSubmitting(true);
    try {
      const result = await verifyMfaLogin(tenantSlug, preSessionId, challengeId, otp);
      if (result.status === 'authenticated') {
        navigate(result.redirectTo);
      } else {
        // The `/session/mfa` route never actually returns `mfa_required` —
        // this branch only satisfies LoginResult's shared union type.
        setError('مشکلی در ارتباط با سرور پیش آمد. دوباره تلاش کنید.');
      }
    } catch (err) {
      if (err instanceof ApiError && err.code === 'VALIDATION_FAILED') {
        setError('کد ۶ رقمی را کامل وارد کنید.');
      } else if (err instanceof ApiError && err.code === 'NOT_FOUND') {
        setError('کد نامعتبر یا منقضی شده است.');
      } else if (err instanceof ApiError && err.code === 'RATE_LIMITED') {
        setError('تعداد تلاش‌ها زیاد بوده — کمی بعد دوباره امتحان کنید.');
      } else {
        setError('مشکلی در ارتباط با سرور پیش آمد. دوباره تلاش کنید.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthSurface
      title="کد تأیید را وارد کنید"
      subtitle={
        state.mobileNumber ? (
          <>
            کد {OTP_LENGTH} رقمی پیامک شد به{' '}
            <span dir="ltr" style={{ fontFamily: 'var(--font-mono)' }}>
              {state.mobileNumber}
            </span>
          </>
        ) : (
          `کد ${OTP_LENGTH} رقمی پیامک شد`
        )
      }
    >
      <form
        onSubmit={(event) => {
          event.preventDefault();
          void onSubmit();
        }}
        noValidate
      >
        <div
          dir="ltr"
          style={{ display: 'flex', gap: 8, justifyContent: 'center', marginBottom: 16 }}
        >
          {digits.map((digit, index) => (
            <input
              key={index}
              ref={(el) => {
                inputRefs.current[index] = el;
              }}
              className="sak-input"
              type="text"
              inputMode="numeric"
              autoComplete={index === 0 ? 'one-time-code' : 'off'}
              maxLength={1}
              value={digit}
              onChange={(event) => onDigitChange(index, event.target.value)}
              onKeyDown={(event) => onDigitKeyDown(index, event)}
              onPaste={onPaste}
              style={{ width: 44, height: 52, textAlign: 'center', fontSize: '20px' }}
              aria-label={`رقم ${index + 1} کد تأیید`}
            />
          ))}
        </div>

        {error ? (
          <Alert tone="danger" title="تأیید ناموفق">
            {error}
          </Alert>
        ) : null}

        <Button type="submit" loading={submitting} style={{ width: '100%', marginTop: 12 }}>
          تأیید
        </Button>
      </form>

      <div
        style={{
          textAlign: 'center',
          marginTop: 16,
          fontSize: 'var(--fs-sm)',
          color: 'var(--text-muted)',
        }}
      >
        {secondsLeft > 0 ? (
          <span>ارسال دوباره تا {formatCountdown(secondsLeft)}</span>
        ) : (
          <button
            type="button"
            onClick={() => navigate(loginPath, { replace: true })}
            style={{
              background: 'none',
              border: 0,
              padding: 0,
              font: 'inherit',
              color: 'var(--brand-neon)',
              textDecoration: 'underline',
              cursor: 'pointer',
            }}
          >
            ارسال دوباره
          </button>
        )}
      </div>
    </AuthSurface>
  );
}
